import { inject } from '@angular/core';
import type { UrlTree } from '@angular/router';
import { type AppPath, AppPathsMap } from '@core/enums/app-paths';
import { MaintenanceService } from '@core/error-pages/maintenance-page/services/maintenance.service';
import { NavigationService } from '@core/navigation/services/navigation.service';
import { RedirectService } from '@core/services/redirect.service';
import { environment } from '@env';
import { LoggerService } from '@kerberos-compliance/compliance-shared-ui';
import type { Observable } from 'rxjs';
import { defer, of, timer } from 'rxjs';
import { catchError, retry, switchMap } from 'rxjs/operators';
import { AuthenticationFacade } from '../../authentication/facades/authentication.facade';

/**
 * A route guard function to enforce authentication and handle user redirection.
 *
 * This function checks the user's authentication status and determines whether navigation
 * should proceed. It handles the following scenarios:
 * - If the user is authenticated, navigation is allowed.
 * - If the application is in maintenance mode, the user is redirected to the maintenance page.
 * - If the user is not authenticated, the current URL is stored for redirection post-login, and
 *   the user is redirected to the login page.
 * - Skips the authentication check for the authentication callback route.
 *
 * @returns {boolean | UrlTree | Observable<boolean | UrlTree>} Returns `true` if the user is allowed to navigate.
 * Otherwise returns a `UrlTree` (directly or via an Observable) redirecting to the maintenance or login page.
 */
export const authenticationGuard = (): boolean | UrlTree | Observable<boolean | UrlTree> => {
  const redirectService = inject(RedirectService);
  const authenticationFacade = inject(AuthenticationFacade);
  const maintenanceService = inject(MaintenanceService);
  const navigationService = inject(NavigationService);
  const loggerService = inject(LoggerService);

  const contextLogger = 'AuthenticationGuard';

  if (environment.e2eMode || !environment.enableMsal) {
    loggerService.warn('AuthGuard: E2E/MSAL-disabled mode, bypassing authentication', contextLogger);
    return true;
  }

  if (maintenanceService.isMaintenanceActive()) {
    return navigationService.createUrlTree([AppPathsMap.Maintenance]);
  }

  const currentUrl = globalThis.location.pathname + globalThis.location.search + globalThis.location.hash;
  loggerService.debug('AuthGuard: Processing URL', contextLogger, {
    pathname: globalThis.location.pathname,
    search: globalThis.location.search,
    hash: globalThis.location.hash,
    fullUrl: currentUrl,
  });

  if ((globalThis.location.pathname as AppPath) === AppPathsMap.AuthCallback) {
    loggerService.debug('🛡️ AuthGuard: This is auth callback, allowing through', contextLogger);
    return true;
  }

  // Check if this is a page reload by looking at the navigation type
  const isPageReload =
    globalThis.performance &&
    (globalThis.performance.navigation.type === 1 || // For older browsers
      globalThis.performance
        .getEntriesByType('navigation')
        .some((nav) => (nav as PerformanceNavigationTiming).type === 'reload'));

  // For Webkit browsers (Chrome, Safari) on page reload, add a small delay and retry mechanism
  // to give MSAL time to rehydrate its state from localStorage
  if (isPageReload && /AppleWebKit/.test(navigator.userAgent)) {
    return defer(() => {
      const checkAuthentication = (): Observable<boolean> => {
        const isAuthenticated = authenticationFacade.isLoggedIn();
        if (isAuthenticated) {
          return of(true);
        }
        throw new Error('Not authenticated yet');
      };

      return timer(50).pipe(
        switchMap(() => checkAuthentication()),
        retry({ count: 2, delay: (error, retryCount) => timer(retryCount * 100) }),
        catchError(() => {
          if ((globalThis.location.pathname as AppPath) !== AppPathsMap.Login) {
            loggerService.debug('🛡️ AuthGuard: User not authenticated, storing URL', contextLogger, {
              currentUrl,
            });
            redirectService.setRedirectUrl(currentUrl);
          }
          return of(navigationService.createUrlTree([AppPathsMap.Login]));
        }),
      );
    });
  }

  // For non-Webkit browsers or non-reload navigation
  return defer(() => {
    const isAuthenticated = authenticationFacade.isLoggedIn();

    if (isAuthenticated) {
      return of(true);
    }

    if ((globalThis.location.pathname as AppPath) !== AppPathsMap.Login) {
      loggerService.debug('🛡️ AuthGuard: User not authenticated, storing URL', contextLogger, { currentUrl });
      redirectService.setRedirectUrl(currentUrl);
    }

    return of(navigationService.createUrlTree([AppPathsMap.Login]));
  });
};
